/**
 * Who the signed-in account is, as the portal chrome prints it: display name,
 * initials, email, and — for owners — the address CoLiviq bills.
 *
 * Extracted from AdminUserMenu so the V3 sidebar's user block and the V2
 * strip's dropdown cannot drift apart. Both render the same four facts, and
 * "the owner's name" resolving one way in the sidebar and another in the menu
 * is the kind of split that goes unnoticed until someone's invoice disagrees
 * with their header.
 *
 * Both query keys are the ones the existing callers already use
 * (`admin-navbar-profile-name`, and Settings → Personal information's
 * `account-billing-address`), so mounting this twice on a page costs nothing
 * and a save in Settings is reflected in the chrome immediately.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useAccountBillingAddress } from "@/components/settings/useAccountBillingAddress";

export type OwnerIdentity = {
  /** Profile name, else signup metadata, else the email's local part. */
  displayName: string;
  /** One or two letters for the avatar. Never empty. */
  initials: string;
  email: string;
  /**
   * The invoice address as lines, empty when there is nothing to show.
   *
   * Only a resolved, non-empty `lessor` address renders. 'ambiguous' and
   * 'none' are states the chrome cannot explain and the owner cannot act on
   * from there, and `null` means this database predates the migration — all
   * three give no lines rather than a placeholder that nags on every open.
   */
  billingLines: string[];
};

/**
 * Initials from a display name: first letters of the first and last words, or
 * the first two characters of a single word. Falls back to "?" so the avatar
 * is never an empty circle.
 */
export function initialsFrom(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * @param withBilling fetch the billing address too. Callers pass
 *   `isOwnerPortalOnly`: staff chrome shows no invoice address, so there is no
 *   reason to make the request for them.
 */
export function useOwnerIdentity(withBilling: boolean): OwnerIdentity {
  const { user } = useAuth();

  const { data: profileRow } = useQuery({
    queryKey: ["admin-navbar-profile-name", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as { name: string | null } | null;
    },
  });

  const metaName =
    typeof user?.user_metadata?.name === "string"
      ? user.user_metadata.name.trim()
      : "";
  const profileName = profileRow?.name?.trim() ?? "";
  const displayName =
    profileName || metaName || user?.email?.split("@")[0]?.trim() || "Account";

  const { data: billing } = useAccountBillingAddress(withBilling);
  const billingLines =
    withBilling && billing?.source === "lessor"
      ? [billing.address, billing.city_postal]
          .map((line) => line?.trim())
          .filter((line): line is string => !!line)
      : [];

  return {
    displayName,
    initials: initialsFrom(displayName),
    email: user?.email ?? "",
    billingLines,
  };
}
